import React from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { ComposedChart, XAxis, YAxis, Area, Tooltip, ResponsiveContainer } from 'recharts';
import styled, { withTheme } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import Loader from '../loader/Loader';

const TimeSeriesContainer = styled.div`
  overflow: hidden;
  width: 100%;
  @media (max-width: 870px) {
    overflow-x: auto;
  }
`;

const TooltipWrapper = styled.div`
  display: table;
  table-layout: fixed;
  overflow: hidden;
  min-width: 256px;
  padding: 8px;
  background-color: white;
  opacity: 0.95;
  border: thin solid ${props => props.theme.borderColor};
`;

const LoaderContainer = styled.div`
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TooltipLine = styled.div`
  display: table-row;
  width: 100%;
`;

const TooltipCell = styled.div`
  display: table-cell;
  empty-cells: show;
`;

const StyledResponsiveContainer = styled(ResponsiveContainer)`
  margin: 0 auto;

  .recharts-cartesian-axis-ticks {
    transform: translateY(8px);
    color: black;
    font-size: 14px;
    font-weight: 500;
  }
`;

const renderTooltip = (e) => {
  if (e.payload[0]) {
    const performance = e.payload[0].payload;
    return (
      <TooltipWrapper>
        <TooltipLine>
          <TooltipCell>
            <FormattedMessage id="TimeSeries.CumulativeTWR" />
          </TooltipCell>
          <TooltipCell>
            {(performance.CumulativeTWR * 100).toFixed(2).replace('.', ',')}%
          </TooltipCell>
        </TooltipLine>
        <TooltipLine>
          <TooltipCell>
            <FormattedMessage id="TimeSeries.Price" />
          </TooltipCell>
          <TooltipCell>{performance.PortfolioPrice.toFixed(2).replace('.', ',')}</TooltipCell>
        </TooltipLine>
        <TooltipLine>
          <TooltipCell>
            <FormattedMessage id="TimeSeries.Date" />
          </TooltipCell>
          <TooltipCell>{moment(performance.Date).format('DD.MM.YYYY')}</TooltipCell>
        </TooltipLine>
        <TooltipLine>
          <TooltipCell>
            <FormattedMessage id="TimeSeries.SeriesType" />
          </TooltipCell>
          <TooltipCell>{performance.SeriesType}</TooltipCell>
        </TooltipLine>
      </TooltipWrapper>
    );
  }

  return <div />;
};

const TimeSeries = ({
  data, loading, height, width,
}) => (
  <TimeSeriesContainer>
    <StyledResponsiveContainer
      debounce={50}
      style={{ margin: '0 auto' }}
      height={height}
      width={width}
    >
      {loading ? (
        <LoaderContainer>
          <Loader size={70} />
        </LoaderContainer>
      ) : (
        <ComposedChart style={{ margin: 'auto' }} data={data}>
          <XAxis
            allowDataOverflow={false}
            minTickGap={50}
            dataKey="Date"
            tickCount={4}
            tickFormatter={e => moment(e).format('DD.MM.YYYY')}
            tick={{ fontFamily: 'Roboto', fontSize: 14, fill: '#000' }}
            stroke="#D1D1D1"
            strokeWidth="1px"
            shapeRendering="crispEdges"
          />
          <YAxis
            hide
            dataKey="CumulativeTWR"
            yAxisId="left"
            tickFormatter={e => `${e * 100}${'%'}`}
            domain={['dataMin', 'dataMax']}
          />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="CumulativeTWR"
            stroke="8884d8"
            fill="#A28352"
          />
          <Tooltip content={renderTooltip} />
        </ComposedChart>
      )}
    </StyledResponsiveContainer>
  </TimeSeriesContainer>
);
TimeSeries.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TimeSeries.defaultProps = {
  loading: false,
  width: '90%',
  height: 240,
};

export default TimeSeries;
